import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND_NAME, getLoginUrl } from "@/const";
import { LogOut, Menu, Palette, ShieldCheck, User, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "wouter";

const NAV = [
  { href: "/", label: "홈" },
  { href: "/create", label: "작품 만들기" },
  { href: "/my", label: "마이페이지" },
];

export function SiteHeader() {
  const { user, isAuthenticated, logout } = useAuth();
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/90 backdrop-blur-lg">
      <div className="container flex h-14 md:h-16 items-center justify-between gap-4">
        <Link
          href="/"
          className="flex items-center gap-2 font-serif text-lg md:text-xl tracking-wide text-primary"
        >
          <Palette className="w-5 h-5 text-amber-700" />
          <span>{BRAND_NAME}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          {NAV.map((item) => {
            const active = location === item.href;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={
                  "transition-colors py-1 " +
                  (active
                    ? "text-primary font-medium border-b-2 border-primary"
                    : "text-muted-foreground hover:text-primary")
                }
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 rounded-full">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="max-w-28 truncate text-sm">
                    {user.name ?? user.email ?? "회원"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{user.email ?? user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/my">마이페이지</Link>
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem asChild>
                    <Link href="/admin">
                      <ShieldCheck className="w-4 h-4 mr-2" /> 관리자
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="w-4 h-4 mr-2" /> 로그아웃
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="rounded-full px-5">
              <a href={getLoginUrl()}>로그인</a>
            </Button>
          )}
        </div>

        <button
          className="md:hidden p-2 -mr-2 text-primary"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="메뉴"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border/50 bg-background animate-in slide-in-from-top-2 duration-200">
          <nav className="container py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="py-2.5 px-3 rounded-lg text-sm text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {isAuthenticated && user?.role === "admin" && (
              <Link
                href="/admin"
                onClick={() => setMobileOpen(false)}
                className="py-2.5 px-3 rounded-lg text-sm text-muted-foreground hover:text-primary hover:bg-primary/5"
              >
                관리자 페이지
              </Link>
            )}
            <div className="pt-3 mt-2 border-t border-border/50">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    logout();
                  }}
                  className="py-2.5 px-3 text-left text-sm text-muted-foreground w-full rounded-lg hover:bg-primary/5"
                >
                  로그아웃
                </button>
              ) : (
                <a
                  href={getLoginUrl()}
                  className="block py-2.5 px-3 text-sm text-primary font-medium rounded-lg hover:bg-primary/5"
                >
                  로그인 / 가입
                </a>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-border/50 bg-muted/40">
      <div className="container py-10 md:py-12 grid gap-8 md:grid-cols-3 text-sm text-muted-foreground">
        <div>
          <div className="flex items-center gap-2 font-serif text-lg text-primary mb-3">
            <Palette className="w-4 h-4 text-amber-700" />
            {BRAND_NAME}
          </div>
          <p className="leading-relaxed text-xs">
            소중한 사진을 나만의 작품으로 만들고<br />
            프리미엄 캔버스에 인화해 드리는 서비스입니다.
          </p>
        </div>
        <div>
          <div className="font-medium text-foreground mb-2 text-xs uppercase tracking-wider">고객센터</div>
          <p className="text-xs">평일 10:00 - 18:00</p>
          <p className="text-xs">contact@oilprint.studio</p>
          <Link href="/support" className="text-xs mt-1 inline-block hover:text-primary underline-offset-2 hover:underline">
            고객센터 / FAQ
          </Link>
        </div>
        <div>
          <div className="font-medium text-foreground mb-2 text-xs uppercase tracking-wider">안내</div>
          <p className="text-xs">결제: 신용카드 · 간편결제</p>
          <p className="text-xs mt-1">배송: 제작 3~5일 후 출고</p>
        </div>
      </div>
      <div className="border-t border-border/40 py-4 text-center text-xs text-muted-foreground/70">
        © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
      </div>
    </footer>
  );
}

export function SitePageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
